import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { kDefaultFolderListLimit, kUserFilesFoldersApiBasePath } from '@/user_files/folders/constants'
import { UserFileProcessingStatus } from '@/user_files/schemas/processing-status'
import { formatUserFilesErrorDetail } from '@/user_files/utils/api-error'
import { FileListResponse, FileListResponseSchema } from '../schemas'

// Stopgap safety cap: keep automatic preloading under roughly 40,000 entries
// until the drive either virtualizes large lists (and this can expand) or
// switches to true UI pagination (and this limit can be removed).
const kMaxFolderFilesPaginationPages = 80
const kFolderFilesPaginationRequestDelayMs = 50

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError'
}

async function getFolderFilesPage(params: {
  cursor: string | null
  folderId: string
  limit: number
  pageIndex: number
  recursive?: boolean
  signal?: AbortSignal
  statuses?: UserFileProcessingStatus[]
  sentryTransactionId: string
  token: string
}): Promise<FileListResponse> {
  const searchParams = new URLSearchParams({
    limit: String(params.limit),
  })
  params.statuses?.forEach((status) => {
    searchParams.append('statuses', status)
  })
  if (params.cursor) {
    searchParams.set('cursor', params.cursor)
  }
  if (params.recursive) {
    searchParams.set('recursive', 'true')
  }

  const url = `${kPaxtonAppApiRESTBaseUrl()}${kUserFilesFoldersApiBasePath}/${encodeURIComponent(params.folderId)}/files?${searchParams.toString()}`
  const response = await fetch(url, {
    method: 'GET',
    signal: params.signal,
    headers: {
      Authorization: `Bearer ${params.token}`,
      'X-Transaction-Id': params.sentryTransactionId,
    },
  })

  const { data, parseError } = await readResponseJson(response)
  if (!response.ok) {
    const errorData = parseError ? {} : data
    const detail = formatUserFilesErrorDetail(errorData)

    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: params.sentryTransactionId })
      Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${detail}`), {
        extra: {
          ...getResponseDebugContext(response, url),
          cursor: params.cursor ?? null,
          folderId: params.folderId,
          limit: params.limit,
          pageIndex: params.pageIndex,
          recursive: params.recursive ?? false,
          responseData: errorData,
          parseError: parseError ? formatUnknownError(parseError) : null,
        },
      })
    })

    throw new ApiError(response.status, detail || `Failed to fetch folder files: ${response.status}`)
  }

  if (parseError) {
    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: params.sentryTransactionId })
      Sentry.captureException(new Error('Folder files response JSON parse failed'), {
        extra: {
          ...getResponseDebugContext(response, url),
          cursor: params.cursor ?? null,
          folderId: params.folderId,
          limit: params.limit,
          pageIndex: params.pageIndex,
          recursive: params.recursive ?? false,
          parseError: formatUnknownError(parseError),
        },
      })
    })

    throw new ApiResponsePayloadError(response.status, 'Invalid folder files response JSON')
  }

  const parsed = FileListResponseSchema.safeParse(data)
  if (!parsed.success) {
    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: params.sentryTransactionId })
      Sentry.captureException(new Error('Folder files response schema validation failed'), {
        extra: {
          ...getResponseDebugContext(response, url),
          cursor: params.cursor ?? null,
          folderId: params.folderId,
          limit: params.limit,
          pageIndex: params.pageIndex,
          recursive: params.recursive ?? false,
          responseKeys: getTopLevelKeys(data),
          issues: parsed.error.issues,
        },
      })
    })

    throw new ApiResponsePayloadError(response.status, 'Invalid folder files response shape', parsed.error.issues)
  }

  return parsed.data
}

/**
 * Get files within a folder.
 *
 * @param folderId - The folder ID (UUID)
 * @param options.limit - Page size for backend pagination (1-500)
 * @param options.recursive - Include files in descendant folders
 * @param options.signal - Optional signal used to abort the request
 * @param options.statuses - Optional repeated statuses query params
 * @returns FileListResponse containing the full list of files
 * @throws Error when user is not authenticated
 * @throws ApiError when the folder-files request fails
 */
export async function getFolderFiles(
  folderId: string,
  options?: {
    limit?: number
    recursive?: boolean
    signal?: AbortSignal
    statuses?: UserFileProcessingStatus[]
  }
): Promise<FileListResponse> {
  const sentryTransactionId = nanoid()
  const limit = options?.limit ?? kDefaultFolderListLimit
  const recursive = options?.recursive
  const signal = options?.signal
  const statuses = options?.statuses

  try {
    const auth = getAuth()
    const token = await auth.currentUser?.getIdToken()

    if (!token) {
      throw new Error('User not authenticated')
    }

    const allFiles: FileListResponse['files'] = []
    let nextCursor: string | null = null
    let pageIndex = 1
    const seenCursors = new Set<string>()

    do {
      if (pageIndex > kMaxFolderFilesPaginationPages) {
        throw new Error(`Exceeded ${kMaxFolderFilesPaginationPages} pages while fetching folder files`)
      }

      if (nextCursor) {
        if (seenCursors.has(nextCursor)) {
          throw new Error('Encountered a repeated pagination cursor while fetching folder files')
        }

        seenCursors.add(nextCursor)
      }

      const page = await getFolderFilesPage({
        cursor: nextCursor,
        folderId,
        limit,
        pageIndex,
        recursive,
        signal,
        statuses,
        sentryTransactionId,
        token,
      })

      allFiles.push(...page.files)
      nextCursor = page.next_cursor
      pageIndex += 1

      if (nextCursor) {
        await new Promise((resolve) => setTimeout(resolve, kFolderFilesPaginationRequestDelayMs))
      }
    } while (nextCursor)

    return {
      files: allFiles,
      folder_id: folderId,
      next_cursor: null,
    }
  } catch (error) {
    if (isAbortError(error)) {
      throw error
    }

    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, {
          extra: {
            folderId,
            limit,
            recursive: recursive ?? false,
            statuses,
          },
        })
      })
    }
    throw error
  }
}
